import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid2,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";

import { Input } from "./Input";
import Loading from "./Loading";

import { checkCep } from "../services/app";
import { parentRegister } from "../services/parents";

import {
  formatCpf,
  formatPhone,
  maskCep,
  phoneValidate,
  unMask,
} from "../helpers/format";

import { cpf } from "cpf-cnpj-validator";
import CheckUserNumber from "./CheckUserNumber";
import { checkUserByPhone } from "../services/user";

const ResponsibleRegister = ({
  open,
  onClose,
  refetch,
}: {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}) => {
  const [value, setValue] = useState("1");

  const [userSelected, setUserSelected] = useState<any>({
    open: false,
    user: null,
    msgErrorPhone: "",
  });

  const { mutate, isPending } = useMutation({
    mutationFn: parentRegister,
    onSuccess: () => {
      onClose();
      refetch();
      toast.success("Responsável cadastrado");
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(
        String(e?.response?.data?.message) ||
          "Não conseguimos cadastrar o responsável"
      );
      onClose();
      refetch();
    },
  });

  const { mutate: checkUserMutate, isPending: isPendingCheckUser } =
    useMutation({
      mutationFn: checkUserByPhone,
      onSuccess: (e) => {
        if (e?.user && !e?.user?.responsableId) {
          setUserSelected({ open: true, user: e?.user, msgErrorPhone: "" });
          return;
        }

        if (e?.user && e?.user?.responsableId) {
          setUserSelected({
            open: false,
            user: null,
            msgErrorPhone: !e.sameStore
              ? "Telefone cadastrado como em outra academia, entre em contato suporte."
              : `Telefone cadastrado com o responsável ${e?.user?.name}.`,
          });
          return;
        }

        setUserSelected({
          open: false,
          user: null,
          msgErrorPhone: "",
        });
      },
      onError: (e: { response: { data: { message: string } } }) => {
        toast.error(String(e?.response?.data?.message) || "Erro!");
      },
    });

  const { values, handleSubmit, handleChange, setFieldValue } = useFormik({
    initialValues: {
      fullName: "",
      nickName: "",
      document: "",
      email: "",
      phone: "",
      state: "",
      city: "",
      birthDate: "",
      neighborhood: "",
      street: "",
      number: "",
      complement: "",
      cep: "",
    },
    onSubmit: (values) => {
      if (value === "1") {
        const requiredFields = [
          { field: values.fullName, label: "Nome" },
          { field: values.document, label: "CPF" },
          { field: values.email, label: "E-mail" },
          { field: values.phone, label: "Telefone" },
          { field: values.cep, label: "CEP" },
          { field: values.street, label: "Rua" },
          { field: values.number, label: "Número" },
          { field: values.neighborhood, label: "Bairro" },
          { field: values.city, label: "Cidade" },
          { field: values.state, label: "Estado" },
        ];

        for (const item of requiredFields) {
          if (!item.field || item.field.trim() === "") {
            toast.error(`Preencha o campo "${item.label}"`);
            return;
          }
        }

        const cleanedCpf = unMask(values.document);
        if (!cpf.isValid(cleanedCpf)) {
          toast.error("CPF inválido");
          return;
        }

        if (!phoneValidate(formatPhone(values.phone))) {
          toast.error("Telefone inválido");
          return;
        }

        mutate({
          ...values,
          phone: unMask(values.phone),
          cep: unMask(values.cep),
          document: cleanedCpf,
        });
      }

      if (value === "2") {
        if (!values.fullName || !values.phone) {
          toast.error("Preencha o nome e telefone");
          return;
        }

        mutate({
          ...values,
          phone: unMask(values.phone),
        });
      }
    },
  });

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const {
    refetch: refetchCep,
    data,
    isFetching,
  } = useQuery({
    queryFn: async () => await checkCep(values.cep),
    queryKey: ["getCep", values.cep],
    enabled: false,
    retry: 1,
  });

  useEffect(() => {
    if (data) {
      setFieldValue("neighborhood", data?.neighborhood);
      setFieldValue("state", data?.state);
      setFieldValue("street", data?.address);
      setFieldValue("city", data?.city);
    }
  }, [data]);

  const checkUser = (e: any) => {
    if (e.target.value) {
      checkUserMutate({ phone: e.target.value.replace(/\D/g, "") });
    }
  };

  return (
    <Dialog maxWidth="sm" open={open} onClose={() => onClose()} fullWidth>
      <Loading loading={isPending || isFetching || isPendingCheckUser} />
      <DialogContent>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 10, md: 11 }}>
            <Typography variant="h2" fontSize={26} fontWeight={600}>
              Cadastrar Responsável
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2, md: 1 }}>
            <IconButton onClick={() => onClose()}>
              <IconX style={styles.iconStyle} />
            </IconButton>
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="subtitle1" color="secondary.contrastText">
              Ao preencher os dados do responsável por aqui, enviaremos um SMS
              para que o mesmo complete o cadastro no app Fight GO pelo celular.
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              sx={{
                borderColor: "divider",
              }}
              onChange={handleChangeValue}
              value={value}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="PREENCHA OS DADOS" value="1" />
              {/* <Tab label="SOLICTAR PREENCHIMENTO AO ALUNO" value="2" /> */}
            </Tabs>
          </Grid2>
          {value === "1" && (
            <>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="fullName"
                  name="fullName"
                  label="Nome"
                  placeholder="Digite o nome completo"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.fullName}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="nickName"
                  name="nickName"
                  label="Apelido (Opcional)"
                  placeholder="Digite o apelido"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.nickName}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="document"
                  name="document"
                  label="CPF"
                  placeholder="Digite o CPF"
                  type="text"
                  fullWidth
                  size="small"
                  value={formatCpf(values.document)}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="birthDate"
                  name="birthDate"
                  label="Nascimento (Opcional)"
                  type="date"
                  fullWidth
                  value={values.birthDate}
                  onChange={handleChange}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                    e.target.showPicker()
                  }
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="phone"
                  name="phone"
                  label="Telefone"
                  placeholder="Digite o telefone"
                  type="text"
                  fullWidth
                  size="small"
                  autoComplete="no-autofill-phone"
                  value={formatPhone(values.phone)}
                  onChange={handleChange}
                  onBlur={checkUser}
                  error={Boolean(userSelected?.msgErrorPhone)}
                  helperText={userSelected?.msgErrorPhone}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="email"
                  name="email"
                  label="E-mail"
                  placeholder="Digite o E-mail"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.email}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={12}>
                <Divider />
              </Grid2>
              <Grid2 size={12}>
                <Typography fontWeight={700}>Endereço</Typography>
              </Grid2>
              <Grid2 size={12}>
                <Input
                  id="cep"
                  name="cep"
                  label="CEP"
                  placeholder="Digite o CEP"
                  type="text"
                  fullWidth
                  size="small"
                  value={maskCep(values.cep)}
                  onChange={handleChange}
                  onBlur={(e) => {
                    if (e.target.value.length) {
                      refetchCep();
                    }
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="street"
                  name="street"
                  label="Rua"
                  placeholder="Digite a rua"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.street}
                  onChange={handleChange}
                />
              </Grid2>{" "}
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Input
                  id="number"
                  name="number"
                  label="Número"
                  placeholder="Digite a número"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.number}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Input
                  id="neighborhood"
                  name="neighborhood"
                  label="Bairro"
                  placeholder="Digite o bairro"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.neighborhood}
                  onChange={handleChange}
                />
              </Grid2>{" "}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="city"
                  name="city"
                  label="Cidade"
                  placeholder="Digite a cidade"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.city}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Input
                  id="state"
                  name="state"
                  label="Estado"
                  placeholder="Digite o estado"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.state}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={12}>
                <Input
                  id="complement"
                  name="complement"
                  label="Complemento (Opcional)"
                  placeholder="Digite o complemento"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.complement}
                  onChange={handleChange}
                />
              </Grid2>
            </>
          )}
          {value === "2" && (
            <>
              <Grid2 size={12}>
                <Input
                  id="fullName"
                  name="fullName"
                  label="Nome"
                  placeholder="Digite o nome completo"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.fullName}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={12}>
                <Input
                  id="phone"
                  name="phone"
                  label="Telefone"
                  placeholder="Digite o telefone"
                  type="text"
                  fullWidth
                  size="small"
                  value={formatPhone(values.phone)}
                  onChange={handleChange}
                />
              </Grid2>{" "}
            </>
          )}
        </Grid2>
      </DialogContent>

      <DialogActions>
        <Grid2 size={12}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => handleSubmit()}
          >
            Cadastrar responsável
          </Button>
        </Grid2>
      </DialogActions>

      <CheckUserNumber
        open={userSelected.open}
        onSubmit={() => {
          mutate({ userId: userSelected?.user?.id });
          setUserSelected((e: any) => ({ ...e, open: false }));
        }}
        user={userSelected.user}
        onClose={() => {
          setUserSelected({ open: false, user: null, msgErrorPhone: "" });
          setFieldValue("phone", "");
        }}
      />
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default ResponsibleRegister;
